import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brush,
  Droplets,
  WashingMachine,
  CheckCircle2,
  Phone,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, PHOTOS, CONTACT } from "@/lib/brand";

const services = [
  {
    title: "Regular Contract Cleaning",
    tagline: "Our most popular service",
    img: PHOTOS.office,
    Icon: Brush,
    points: [
      "Daily, weekly or bi-weekly schedules",
      "Same trusted team each visit",
      "Custom cleaning spec per site",
      "Quality checks & client reporting",
    ],
    desc: "A dependable cleaning routine that keeps your premises consistently presentable. We agree a checklist tailored to your business and stick to it — visit after visit.",
    highlight: true,
  },
  {
    title: "Deep Cleaning",
    tagline: "Reset & restore",
    img: PHOTOS.gym,
    Icon: WashingMachine,
    points: [
      "Top-to-bottom sanitisation",
      "Kitchens, washrooms & high-touch points",
      "Hard-floor scrubbing & polishing",
      "Ideal seasonally or pre-inspection",
    ],
    desc: "A thorough, room-by-room reset for premises that need more than a routine clean — perfect for end-of-season, post-build, or pre-inspection.",
  },
  {
    title: "Window Cleaning",
    img: PHOTOS.retail,
    tagline: "Bright. Streak-free.",
    Icon: Droplets,
    points: [
      "Interior and exterior glass",
      "High-reach safe equipment",
      "Storefronts, offices and signage",
      "Scheduled rounds available",
    ],
    desc: "Clean windows transform a space — and a storefront. We keep your glass crystal clear with regular or one-off rounds across Cork.",
  },
];

export default function Services() {
  return (
    <div data-testid="page-services">
      {/* HERO */}
      <section className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-sm font-semibold tracking-widest uppercase text-[#388E3C]">
                Services
              </p>
              <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
                Cleaning services{" "}
                <span className="text-[#4CAF50]">built around your business.</span>
              </h1>
              <p className="mt-5 text-slate-600 text-base sm:text-lg max-w-2xl leading-relaxed">
                Whether you need a reliable cleaning rota, a deep reset, or
                streak-free windows — our Cork team delivers consistent,
                professional results.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/contact">
                  <Button
                    data-testid="services-hero-quote"
                    className="h-12 px-7 rounded-full bg-[#4CAF50] hover:bg-[#43A047] text-white font-semibold btn-primary-glow"
                  >
                    Get Your Free Quote
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <a href={`tel:${CONTACT.phoneTel}`}>
                  <Button
                    data-testid="services-hero-call"
                    variant="outline"
                    className="h-12 px-6 rounded-full border-slate-300 hover:bg-slate-50 font-semibold"
                  >
                    <Phone className="h-4 w-4 mr-2 text-[#4CAF50]" />
                    {CONTACT.phone}
                  </Button>
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative h-80 flex items-center justify-center">
                <div
                  aria-hidden
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                >
                  <div className="h-64 w-64 rounded-full bg-[#4CAF50]/15 blur-3xl" />
                </div>
                <img
                  src={BRAND.mascot}
                  alt="Clean Me mascot"
                  className="relative z-10 h-full w-full object-contain drop-shadow-[0_20px_40px_rgba(15,23,42,0.18)]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE BLOCKS */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24 space-y-16 sm:space-y-20">
          {services.map((s, i) => {
            const reverse = i % 2 === 1;
            return (
              <article
                key={s.title}
                data-testid={`services-block-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center"
              >
                <div
                  className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}
                >
                  <div className="rounded-[28px] overflow-hidden shadow-soft-lg">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="w-full h-72 sm:h-96 object-cover"
                    />
                  </div>
                </div>
                <div
                  className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}
                >
                  {s.highlight && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#4CAF50] text-white text-xs font-semibold px-3 py-1">
                      Most popular
                    </span>
                  )}
                  <div className="mt-4 inline-flex items-center gap-3">
                    <span className="h-12 w-12 rounded-2xl bg-[#4CAF50]/12 inline-flex items-center justify-center text-[#388E3C]">
                      <s.Icon className="h-6 w-6" />
                    </span>
                    <p className="text-sm font-semibold tracking-widest uppercase text-[#388E3C]">
                      {s.tagline}
                    </p>
                  </div>
                  <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-slate-600 leading-relaxed">
                    {s.desc}
                  </p>
                  <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                    {s.points.map((p) => (
                      <li
                        key={p}
                        className="flex items-start gap-2.5 text-slate-800"
                      >
                        <CheckCircle2 className="h-5 w-5 text-[#4CAF50] flex-shrink-0 mt-0.5" />
                        <span className="text-sm font-medium">{p}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="mt-7 inline-block">
                    <Button
                      data-testid={`services-block-cta-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                      className="h-12 px-7 rounded-full bg-[#4CAF50] hover:bg-[#43A047] text-white font-semibold btn-primary-glow"
                    >
                      Get Started Today
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
          <div className="rounded-3xl bg-slate-900 text-white p-8 sm:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6 shadow-soft-lg">
            <div className="flex items-start gap-4">
              <span className="h-12 w-12 rounded-2xl bg-[#4CAF50]/20 inline-flex items-center justify-center text-[#A5D6A7]">
                <Sparkles className="h-6 w-6" />
              </span>
              <div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
                  Not sure which service fits?
                </h3>
                <p className="mt-1 text-slate-300">
                  Tell us about your space — we'll recommend the right plan.
                </p>
              </div>
            </div>
            <Link to="/contact">
              <Button
                data-testid="services-bottom-cta"
                className="h-12 px-7 rounded-full bg-[#4CAF50] hover:bg-[#43A047] text-white font-semibold"
              >
                Get Your Free Quote
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
